import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { nanoid } from 'nanoid';
import { validateAskRequest } from '@src/components/nexa/utils/validateAskRequest';
import { setPendingAskRequest } from '@src/components/nexa/utils/askNexaStore';
import { logger } from '@src/utils/misc/logger';

const { baseUrl } = window.conviva;

/**
 * Entry point for "Ask Nexa" requests from various dashboard contexts
 * Handles /nx/ask?req=<base64> route
 * Decodes request, validates, and redirects to Nexa with state
 */
const AskNexa = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    try {
      // Get the encoded request from query params
      const encodedReq = searchParams.get('req');

      if (!encodedReq) {
        logger.error('[AskNexa] No request parameter found');
        // Redirect to Nexa without prefill
        const id = nanoid();
        navigate(`${baseUrl}/dashboard/nexa/${id}`, { replace: true });
        return;
      }

      // Decode base64 request
      let decodedReq;
      try {
        const jsonString = atob(encodedReq);
        decodedReq = JSON.parse(jsonString);
      } catch (decodeError) {
        logger.error('[AskNexa] Failed to decode request:', decodeError);
        // Redirect to Nexa without prefill
        const id = nanoid();
        navigate(`${baseUrl}/dashboard/nexa/${id}`, { replace: true });
        return;
      }

      // Validate request structure
      const validation = validateAskRequest(decodedReq);
      if (!validation.valid) {
        logger.error('[AskNexa] Invalid request:', validation.error);
        // Redirect to Nexa without prefill
        const id = nanoid();
        navigate(`${baseUrl}/dashboard/nexa/${id}`, { replace: true });
        return;
      }

      // Generate new session ID
      const sessionId = nanoid();

      logger.log('[AskNexa] Redirecting with request for session:', sessionId, decodedReq);

      // Store request in memory and redirect to Nexa
      // Using store instead of router state to avoid auto-trigger on tab duplication
      setPendingAskRequest(sessionId, decodedReq);
      navigate(`${baseUrl}/dashboard/nexa/${sessionId}`, {
        replace: true,
      });
    } catch (error) {
      logger.error('[AskNexa] Unexpected error:', error);
      // Fallback: redirect to Nexa without prefill
      const id = nanoid();
      navigate(`${baseUrl}/dashboard/nexa/${id}`, { replace: true });
    }
  }, [navigate, searchParams]);

  // Show nothing while processing
  return null;
};

export default AskNexa;
